export const MAX_TEXT = 1000;
export const MAX_PHOTOS = 20;
/** Запасные дефолты, если каталог пресетов ещё не загрузился / упал. */
const FALLBACK = {
    background: "#FBEAF0",
    text: "#1A1726",
    greeting: "С любовью!",
    font: "caveat",
    animation: "shine",
};
export function initialEditorState(type = "gift_page") {
    return {
        type,
        background: { kind: "custom", hex: FALLBACK.background },
        patternIds: [],
        patternSize: 48,
        patternVariety: 1,
        greetingText: { kind: "custom", text: FALLBACK.greeting },
        greetingFont: FALLBACK.font,
        greetingColor: { kind: "custom", hex: FALLBACK.text },
        greetingSize: 36,
        occasion: { kind: "none" },
        iconId: null,
        recipient: "",
        sender: "",
        wish: { kind: "none" },
        boxColorId: null,
        animationKey: FALLBACK.animation,
        captionText: FALLBACK.greeting,
        captionFont: FALLBACK.font,
        captionColor: { kind: "custom", hex: FALLBACK.text },
        captionSize: 32,
    };
}
/**
 * Стартовое оформление из каталога пресетов: фон, узор, текст, коробка,
 * анимация — чтобы превью сразу выглядело «живым», а не пустым.
 */
export function defaultEditorState(presets, type = "gift_page") {
    const bg = presets.backgrounds.find((b) => /розов|pink|fbeaf0/i.test(`${b.name} ${b.color_hex}`)) ?? presets.backgrounds[0];
    const pattern = presets.patterns.find((p) => /звёзд|звезд|star/i.test(p.name)) ??
        presets.patterns[0];
    const textColor = presets.text_colors[0];
    const font = presets.fonts.find((f) => f.key === "caveat") ?? presets.fonts[0];
    const greeting = presets.greetings[0];
    const box = presets.box_colors.find((b) => /красн|red|ee4343|розов|pink/i.test(`${b.name} ${b.color_hex}`)) ?? presets.box_colors[0];
    const anim = presets.animations.find((a) => a.key === "shine") ??
        presets.animations[0];
    const greetingLabel = greeting?.text?.trim() || FALLBACK.greeting;
    const fontKey = font?.key || FALLBACK.font;
    return {
        ...initialEditorState(type),
        background: bg
            ? { kind: "preset", id: bg.id }
            : { kind: "custom", hex: FALLBACK.background },
        patternIds: pattern ? [pattern.id] : [],
        patternSize: 48,
        patternVariety: 1,
        greetingText: greeting
            ? { kind: "preset", id: greeting.id }
            : { kind: "custom", text: FALLBACK.greeting },
        greetingFont: fontKey,
        greetingColor: textColor
            ? { kind: "preset", id: textColor.id }
            : { kind: "custom", hex: FALLBACK.text },
        greetingSize: 36,
        boxColorId: box?.id ?? null,
        animationKey: anim?.key || FALLBACK.animation,
        captionText: greetingLabel.slice(0, 120),
        captionFont: fontKey,
        captionColor: textColor
            ? { kind: "preset", id: textColor.id }
            : { kind: "custom", hex: FALLBACK.text },
        captionSize: 32,
    };
}
function colorToValue(c) {
    if (c.kind === "preset")
        return { preset_id: c.id };
    if (c.kind === "custom" && c.hex.trim())
        return { color_hex: c.hex.trim() };
    return undefined;
}
function textToValue(c) {
    if (c.kind === "preset")
        return { preset_id: c.id };
    if (c.kind === "custom" && c.text.trim())
        return { text: c.text.trim() };
    return undefined;
}
function titleToValue(c) {
    if (c.kind === "preset")
        return { preset_id: c.id };
    if (c.kind === "custom" && c.title.trim())
        return { title: c.title.trim() };
    return undefined;
}
/** Есть ли хоть какое-то значение в выборе. */
export function hasChoice(c) {
    if (c.kind === "none")
        return false;
    if (c.kind === "preset")
        return true;
    if ("hex" in c)
        return Boolean(c.hex.trim());
    if ("text" in c)
        return Boolean(c.text.trim());
    if ("title" in c)
        return Boolean(c.title.trim());
    return false;
}
/**
 * Собирает config под текущий тип. Опциональные блоки включаются только когда
 * заполнены обязательные под-поля (иначе бэк вернёт 422). Фото сюда не входят —
 * они добавляются отдельной ручкой после создания шары.
 */
export function buildConfig(s) {
    const background = colorToValue(s.background);
    const patterns = s.patternIds.length > 0
        ? {
            preset_ids: s.patternIds,
            pattern_size: s.patternSize,
            pattern_variety: s.patternVariety,
        }
        : undefined;
    if (s.type === "simple_page") {
        const captionColor = colorToValue(s.captionColor);
        const caption = s.captionText.trim() && s.captionFont && captionColor
            ? {
                text: s.captionText.trim(),
                font_key: s.captionFont,
                text_color: captionColor,
                ...(s.captionSize ? { text_size: s.captionSize } : {}),
            }
            : undefined;
        const config = {
            ...(background ? { background } : {}),
            ...(patterns ? { patterns } : {}),
            ...(caption ? { caption } : {}),
        };
        return config;
    }
    // gift_page
    const greetingTextValue = textToValue(s.greetingText);
    const greetingColorValue = colorToValue(s.greetingColor);
    const greeting = greetingTextValue && s.greetingFont && greetingColorValue
        ? {
            text: greetingTextValue,
            font_key: s.greetingFont,
            text_color: greetingColorValue,
            text_size: s.greetingSize,
        }
        : undefined;
    const config = {
        ...(background ? { background } : {}),
        ...(patterns ? { patterns } : {}),
        ...(greeting ? { greeting } : {}),
        ...(titleToValue(s.occasion) ? { occasion: titleToValue(s.occasion) } : {}),
        ...(s.iconId != null ? { icon: { preset_id: s.iconId } } : {}),
        ...(s.recipient.trim() ? { recipient: s.recipient.trim() } : {}),
        ...(s.sender.trim() ? { sender: s.sender.trim() } : {}),
        ...(textToValue(s.wish) ? { wish: textToValue(s.wish) } : {}),
        ...(s.boxColorId != null ? { box_color: { preset_id: s.boxColorId } } : {}),
        ...(s.animationKey
            ? { open_animation: { animation_key: s.animationKey } }
            : {}),
    };
    return config;
}
/**
 * Мягкая клиентская валидация — ловит частично заполненные блоки, где текст
 * есть, а обязательные шрифт/цвет не выбраны (иначе бэк молча не сохранит блок
 * или вернёт 422). Возвращает список сообщений (пусто = ок).
 */
export function validateEditor(s) {
    const errors = [];
    if (s.type === "gift_page") {
        if (hasChoice(s.greetingText) && (!s.greetingFont || !hasChoice(s.greetingColor))) {
            errors.push("Для поздравления выберите шрифт и цвет текста.");
        }
    }
    else {
        if (s.captionText.trim() && (!s.captionFont || !hasChoice(s.captionColor))) {
            errors.push("Для подписи выберите шрифт и цвет текста.");
        }
    }
    return errors;
}
